import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
// Import axiosConfig early to ensure API baseURL is configured before any API calls
import './utils/axiosConfig';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ErrorBoundary from './components/ErrorBoundary';
import NotificationContainer from './components/NotificationContainer';
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import Quiz from './pages/Quiz';
import Compete from './pages/Compete';
import Leaderboard from './pages/Leaderboard';
import Profile from './pages/Profile';
import Share from './pages/Share';
import About from './pages/About';
import ContactUs from './pages/ContactUs';
import Premium from './pages/Premium';
import PremiumQuizzes from './pages/PremiumQuizzes';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import CookiePolicy from './pages/CookiePolicy';
import Disclaimer from './pages/Disclaimer';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import './App.css';
import AdminLayout from './components/AdminLayout';
import AdminQuestionReview from './pages/AdminQuestionReview';
import AdminDashboard from './pages/AdminDashboard';
import AdminCompetitive from './pages/AdminCompetitive';
import AdminUpcomingCompetitive from './pages/AdminUpcomingCompetitive';
import AdminUsers from './pages/AdminUsers';
import AdminContentManager from './pages/AdminContentManager';
import AdminDifficultyManager from './pages/AdminDifficultyManager';
import AdminContactSubmissions from './pages/AdminContactSubmissions';

// Protected Route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }
  
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

// Admin Route component (requires authenticated user with isAdmin flag)
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.isAdmin !== true) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// Public Route component (redirects if already authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }
  
  return isAuthenticated ? <Navigate to="/" replace /> : <>{children}</>;
};

function AppContent() {
  const location = useLocation();
  const isSharePage = location.pathname.startsWith('/share/');
  const isAdminPage = location.pathname.startsWith('/admin');

  return (
    <div className="App">
      <NotificationContainer />
      <Navbar />
      <main className={`app-shell${isSharePage ? ' app-shell--share' : ''}${isAdminPage ? ' app-shell--admin' : ' container'}`}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<ContactUs />} />
          <Route path="/premium" element={<Premium />} />
          <Route
            path="/premium/quizzes"
            element={
              <ProtectedRoute>
                <PremiumQuizzes />
              </ProtectedRoute>
            }
          />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/disclaimer" element={<Disclaimer />} />
          <Route path="/share/:token" element={<Share />} />
          <Route 
            path="/login" 
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            } 
          />
          <Route 
            path="/register" 
            element={
              <PublicRoute>
                <Register />
              </PublicRoute>
            } 
          />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route
            path="/forgot-password"
            element={
              <PublicRoute>
                <ForgotPassword />
              </PublicRoute>
            }
          />
          <Route
            path="/reset-password"
            element={
              <PublicRoute>
                <ResetPassword />
              </PublicRoute>
            }
          />
          <Route path="/compete" element={<Compete />} />
          <Route path="/quiz" element={<ErrorBoundary title="Quiz error"><Quiz /></ErrorBoundary>} />
          <Route path="/leaderboard" element={<Leaderboard />} />
          <Route 
            path="/profile" 
            element={
              <ProtectedRoute>
                <ErrorBoundary title="Profile error">
                  <Profile />
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <ErrorBoundary title="Admin dashboard error">
                  <AdminLayout><AdminDashboard /></AdminLayout>
                </ErrorBoundary>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/question-coverage"
            element={
              <AdminRoute>
                <Navigate to="/admin/content" replace />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/question-review"
            element={
              <AdminRoute>
                <ErrorBoundary title="Admin review queue error">
                  <AdminLayout><AdminQuestionReview /></AdminLayout>
                </ErrorBoundary>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/competitive"
            element={
              <AdminRoute>
                <ErrorBoundary title="Admin competitive error">
                  <AdminLayout><AdminCompetitive /></AdminLayout>
                </ErrorBoundary>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/competitive-upcoming"
            element={
              <AdminRoute>
                <ErrorBoundary title="Admin upcoming competitive error">
                  <AdminLayout><AdminUpcomingCompetitive /></AdminLayout>
                </ErrorBoundary>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <AdminRoute>
                <ErrorBoundary title="Admin users error">
                  <AdminLayout><AdminUsers /></AdminLayout>
                </ErrorBoundary>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/contact-submissions"
            element={
              <AdminRoute>
                <ErrorBoundary title="Admin contact submissions error">
                  <AdminLayout><AdminContactSubmissions /></AdminLayout>
                </ErrorBoundary>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/content"
            element={
              <AdminRoute>
                <ErrorBoundary title="Admin content manager error">
                  <AdminLayout><AdminContentManager /></AdminLayout>
                </ErrorBoundary>
              </AdminRoute>
            }
          />
          <Route
            path="/admin/difficulty-manager"
            element={
              <AdminRoute>
                <ErrorBoundary title="Admin difficulty manager error">
                  <AdminLayout><AdminDifficultyManager /></AdminLayout>
                </ErrorBoundary>
              </AdminRoute>
            }
          />
        </Routes>
      </main>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <NotificationProvider>
        <Router>
          <AppContent />
        </Router>
      </NotificationProvider>
    </AuthProvider>
  );
}

export { ProtectedRoute, PublicRoute, AppContent };
export default App;
